import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const emptyRule = () => ({
  key: Math.random().toString(36).slice(2),
  targetType: 'group',
  groupName: '@all',
  userId: '',
  applyHere: true,
  applySub: true,
  allow: {},
  deny: {},
});

const AclManager = () => {
  const [flatChannels, setFlatChannels] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedChannelId, setSelectedChannelId] = useState('');
  const [channelGroups, setChannelGroups] = useState([]);
  const [rules, setRules] = useState([]);
  const [inherit, setInherit] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingAcls, setLoadingAcls] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applyStatus, setApplyStatus] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [channelsRes, permsRes] = await Promise.all([
          api.get('/mumble-admin/channels'),
          api.get('/mumble-admin/permissions'),
        ]);
        setFlatChannels(channelsRes.data.flat);
        setPermissions(permsRes.data.permissions);
        if (channelsRes.data.flat.length > 0) {
          setSelectedChannelId(String(channelsRes.data.flat[0].channel_id));
        }
      } catch (error) {
        toast.error('Failed to load channels/permissions');
        console.error(error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const loadChannelAcls = useCallback(async (channelId) => {
    if (channelId === '' || channelId === undefined) return;
    setLoadingAcls(true);
    try {
      const [aclsRes, groupsRes, channelRes] = await Promise.all([
        api.get(`/mumble-admin/channels/${channelId}/acls`),
        api.get(`/mumble-admin/channels/${channelId}/groups`),
        api.get(`/mumble-admin/channels/${channelId}`),
      ]);
      setChannelGroups(groupsRes.data.groups);
      setInherit(!!channelRes.data.channel.inheritacl);

      const loaded = aclsRes.data.acls.map((row) => {
        const allow = {};
        const deny = {};
        permissions.forEach((p) => {
          if (row.grantpriv & p.value) allow[p.name] = true;
          if (row.revokepriv & p.value) deny[p.name] = true;
        });
        return {
          key: `acl-${row.acl_id}`,
          targetType: row.user_id >= 0 ? 'user' : 'group',
          groupName: row.group_name || '@all',
          userId: row.user_id >= 0 ? String(row.user_id) : '',
          applyHere: !!row.apply_here,
          applySub: !!row.apply_sub,
          allow,
          deny,
        };
      });
      setRules(loaded);
    } catch (error) {
      toast.error('Failed to load ACLs for this channel');
      console.error(error);
    } finally {
      setLoadingAcls(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions]);

  useEffect(() => {
    if (selectedChannelId !== '' && permissions.length > 0) {
      loadChannelAcls(selectedChannelId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChannelId, permissions.length]);

  const updateRule = (key, changes) => {
    setRules((prev) => prev.map((r) => (r.key === key ? { ...r, ...changes } : r)));
  };

  const togglePermission = (key, bucket, permName) => {
    setRules((prev) =>
      prev.map((r) => {
        if (r.key !== key) return r;
        const next = { ...r[bucket] };
        if (next[permName]) {
          delete next[permName];
        } else {
          next[permName] = true;
        }
        return { ...r, [bucket]: next };
      })
    );
  };

  const addRule = () => setRules((prev) => [...prev, emptyRule()]);
  const removeRule = (key) => setRules((prev) => prev.filter((r) => r.key !== key));

  const permBitmask = (permSet, names) =>
    names.reduce((mask, name) => {
      const perm = permissions.find((p) => p.name === name);
      return perm && permSet[name] ? mask | perm.value : mask;
    }, 0);

  const handleSave = async () => {
    const allNames = permissions.map((p) => p.name);
    const payload = {
      inherit,
      acls: rules.map((r) => ({
        userId: r.targetType === 'user' ? parseInt(r.userId, 10) : -1,
        groupName: r.targetType === 'group' ? r.groupName : null,
        applyHere: r.applyHere ? 1 : 0,
        applySub: r.applySub ? 1 : 0,
        grantPriv: permBitmask(r.allow, allNames),
        revokePriv: permBitmask(r.deny, allNames),
      })),
    };

    setSaving(true);
    setApplyStatus('Saving permission rules…');
    try {
      await api.put(`/mumble-admin/channels/${selectedChannelId}/acl`, payload);
      setApplyStatus('Restarting voice server so changes take effect…');
      await api.post('/mumble-admin/restart-voice-server');

      // Poll until the API is responsive again before declaring success.
      const deadline = Date.now() + 15000;
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        try {
          await api.get('/mumble-admin/channels');
          break;
        } catch {
          // still restarting, keep polling
        }
      }

      toast.success('Permissions saved and applied');
      setApplyStatus(null);
      loadChannelAcls(selectedChannelId);
    } catch (error) {
      toast.error('Failed to save permissions');
      console.error(error);
      setApplyStatus(null);
    } finally {
      setSaving(false);
    }
  };

  const basicPerms = permissions.filter((p) => !p.advanced);
  const advancedPerms = permissions.filter((p) => p.advanced);

  if (loading) {
    return <div className="loading">Loading permissions…</div>;
  }

  return (
    <div className="acl-manager">
      <div className="acl-toolbar">
        <div className="form-group">
          <label>Channel</label>
          <select
            className="form-control"
            value={selectedChannelId}
            onChange={(e) => setSelectedChannelId(e.target.value)}
          >
            {flatChannels.map((c) => (
              <option key={c.channel_id} value={c.channel_id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <label className="inherit-toggle">
          <input type="checkbox" checked={inherit} onChange={(e) => setInherit(e.target.checked)} />
          Inherit permissions from parent channel
        </label>
      </div>

      {loadingAcls ? (
        <div className="loading">Loading rules…</div>
      ) : (
        <>
          <div className="rules-list">
            {rules.length === 0 && <p className="empty-state">No rules yet — add one below.</p>}

            {rules.map((rule) => (
              <div key={rule.key} className="rule-card">
                <div className="rule-header">
                  <div className="target-picker">
                    <select
                      className="form-control"
                      value={rule.targetType}
                      onChange={(e) => updateRule(rule.key, { targetType: e.target.value })}
                    >
                      <option value="group">Group</option>
                      <option value="user">Specific user (by ID)</option>
                    </select>

                    {rule.targetType === 'group' ? (
                      <select
                        className="form-control"
                        value={rule.groupName}
                        onChange={(e) => updateRule(rule.key, { groupName: e.target.value })}
                      >
                        <option value="@all">@all (everyone)</option>
                        {channelGroups.map((g) => (
                          <option key={g.group_id} value={g.name}>
                            {g.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Mumble user ID"
                        value={rule.userId}
                        onChange={(e) => updateRule(rule.key, { userId: e.target.value })}
                      />
                    )}
                  </div>
                  <button className="btn btn-sm btn-danger" onClick={() => removeRule(rule.key)}>
                    Remove Rule
                  </button>
                </div>

                <div className="apply-toggles">
                  <label>
                    <input
                      type="checkbox"
                      checked={rule.applyHere}
                      onChange={(e) => updateRule(rule.key, { applyHere: e.target.checked })}
                    />
                    Applies to this channel
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={rule.applySub}
                      onChange={(e) => updateRule(rule.key, { applySub: e.target.checked })}
                    />
                    Applies to sub-channels
                  </label>
                </div>

                <div className="permission-columns">
                  <div className="permission-column allow-column">
                    <h4>Allow</h4>
                    {basicPerms.map((p) => (
                      <label key={p.name}>
                        <input
                          type="checkbox"
                          checked={!!rule.allow[p.name]}
                          onChange={() => togglePermission(rule.key, 'allow', p.name)}
                        />
                        {p.label}
                      </label>
                    ))}
                  </div>
                  <div className="permission-column deny-column">
                    <h4>Deny</h4>
                    {basicPerms.map((p) => (
                      <label key={p.name}>
                        <input
                          type="checkbox"
                          checked={!!rule.deny[p.name]}
                          onChange={() => togglePermission(rule.key, 'deny', p.name)}
                        />
                        {p.label}
                      </label>
                    ))}
                  </div>
                </div>

                {showAdvanced && (
                  <div className="permission-columns advanced">
                    <div className="permission-column allow-column">
                      <h4>Allow (Advanced)</h4>
                      {advancedPerms.map((p) => (
                        <label key={p.name}>
                          <input
                            type="checkbox"
                            checked={!!rule.allow[p.name]}
                            onChange={() => togglePermission(rule.key, 'allow', p.name)}
                          />
                          {p.label}
                        </label>
                      ))}
                    </div>
                    <div className="permission-column deny-column">
                      <h4>Deny (Advanced)</h4>
                      {advancedPerms.map((p) => (
                        <label key={p.name}>
                          <input
                            type="checkbox"
                            checked={!!rule.deny[p.name]}
                            onChange={() => togglePermission(rule.key, 'deny', p.name)}
                          />
                          {p.label}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="acl-actions">
            <button className="btn btn-secondary" onClick={addRule}>
              + Add Rule
            </button>
            <button className="btn btn-link" onClick={() => setShowAdvanced((v) => !v)}>
              {showAdvanced ? 'Hide' : 'Show'} advanced permissions
            </button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save & Apply'}
            </button>
          </div>

          {applyStatus && <div className="apply-status">{applyStatus}</div>}
        </>
      )}

      <style jsx>{`
        .acl-manager {
          padding: 20px;
        }
        .acl-toolbar {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          align-items: flex-end;
          margin-bottom: 20px;
        }
        .inherit-toggle {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 500;
        }
        .empty-state {
          color: #6c757d;
        }
        .rule-card {
          border: 1px solid #ddd;
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 16px;
          background: #fff;
        }
        .rule-header {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 12px;
        }
        .target-picker {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          flex: 1;
          min-width: 200px;
        }
        .target-picker select,
        .target-picker input {
          min-width: 140px;
          flex: 1;
        }
        .apply-toggles {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          margin-bottom: 12px;
          font-size: 0.9rem;
        }
        .apply-toggles label {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .permission-columns {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        .permission-columns.advanced {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px dashed #ddd;
        }
        .permission-column h4 {
          margin: 0 0 8px 0;
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          color: #6c757d;
        }
        .allow-column h4 {
          color: #28a745;
        }
        .deny-column h4 {
          color: #dc3545;
        }
        .permission-column label {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          padding: 4px 0;
          font-size: 0.9rem;
        }
        .acl-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          align-items: center;
          margin-top: 16px;
        }
        .btn-link {
          background: none;
          border: none;
          color: #007bff;
          cursor: pointer;
          padding: 8px 4px;
        }
        .apply-status {
          margin-top: 12px;
          padding: 10px 14px;
          background-color: #e7f3ff;
          border: 1px solid #b6d9ff;
          border-radius: 6px;
          color: #084298;
        }
        .form-group {
          margin-bottom: 0;
        }
        .form-group label {
          display: block;
          margin-bottom: 5px;
          font-weight: 500;
        }
        .form-control {
          padding: 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
        }
        .btn {
          padding: 8px 16px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 500;
        }
        .btn-primary {
          background-color: #007bff;
          color: white;
        }
        .btn-secondary {
          background-color: #6c757d;
          color: white;
        }
        .btn-danger {
          background-color: #dc3545;
          color: white;
        }
        .btn-sm {
          padding: 4px 8px;
          font-size: 0.875rem;
        }

        /* Mobile: stack allow/deny columns instead of side-by-side */
        @media (max-width: 600px) {
          .permission-columns {
            grid-template-columns: 1fr;
          }
          .acl-toolbar {
            flex-direction: column;
            align-items: stretch;
          }
          .target-picker select,
          .target-picker input {
            min-width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default AclManager;
